import { useEffect, useRef } from "react";
import { Navigate } from "react-router-dom";

import { CheckboxGroup, Span } from "@icg360/design-system";

import { MultiStepFormLayout } from "components/multi-step-form-layout";
import { useSelectedPolicy } from "data/ss-data-hooks";
import { useMultiStepFormState } from "hooks/use-multi-step-form-state";

import {
  formatWindDefensePolicyCardStreet,
  formatWindDefensePolicyCardSubtitle,
  windDefensePolicyCardUiIcon,
} from "./policy-helpers";
import styles from "./shingle-reseal.module.scss";
import {
  WindDefenseContext,
  type WindDefenseContextState,
} from "./wind-defense-flow";

const STEP_LABELS = [
  "Select properties",
  "Confirm details",
  "Review and finish",
];

export const MultiPolicyConfirmation = () => {
  const { data: selectedPolicy } = useSelectedPolicy();
  const { state, setFields, formNavigate } =
    useMultiStepFormState<WindDefenseContextState>(WindDefenseContext);
  const defaultsAppliedRef = useRef(false);
  const sessionPolicyId = selectedPolicy?.keystonePolicyId;
  const eligibleWindDefensePolicies = state?.eligibleWindDefensePolicies;
  const windDefenseEligiblePolicies = eligibleWindDefensePolicies ?? [];

  useEffect(() => {
    const list = eligibleWindDefensePolicies ?? [];
    if (!list.length || defaultsAppliedRef.current) {
      return;
    }
    if (state?.selectedWindDefensePolicyIds !== undefined) {
      defaultsAppliedRef.current = true;
      return;
    }
    defaultsAppliedRef.current = true;
    const sessionInList = list.some(
      (p) => p.keystonePolicyId === sessionPolicyId,
    );
    const nextIds =
      list.length === 1
        ? [list[0].keystonePolicyId]
        : sessionInList && sessionPolicyId
          ? [sessionPolicyId]
          : [];
    setFields({ selectedWindDefensePolicyIds: nextIds });
  }, [
    sessionPolicyId,
    setFields,
    state?.selectedWindDefensePolicyIds,
    eligibleWindDefensePolicies,
  ]);

  const selectedIds = state?.selectedWindDefensePolicyIds ?? [];
  if (!windDefenseEligiblePolicies.length) {
    return <Navigate to="/my/property-services/wind-defense" replace />;
  }

  if (windDefenseEligiblePolicies.length === 1) {
    return <Navigate to="../contact-information" replace />;
  }

  const onSelectionChange = (ids: string[]) => {
    setFields({ selectedWindDefensePolicyIds: ids });
  };

  return (
    <MultiStepFormLayout
      showLogo
      labels={STEP_LABELS}
      step={1}
      totalSteps={3}
      heading="Choose which properties you want to enroll in Wind Defense"
      buttonProps={{
        primary: {
          path: "../contact-information",
          disabled: selectedIds.length === 0,
        },
        cancel: { path: "/my/property-services/wind-defense" },
        promptOnCancel: true,
        formNavigate,
      }}
    >
      <CheckboxGroup
        className={styles.wdPropertyCheckboxGroup}
        value={selectedIds}
        onChange={onSelectionChange}
        aria-label="Properties to receive Wind Defense"
      >
        {windDefenseEligiblePolicies.map((policy) => (
          <CheckboxGroup.Card
            className={styles.wdPropertyCheckboxCard}
            key={policy.keystonePolicyId}
            value={policy.keystonePolicyId}
            uiIcon={windDefensePolicyCardUiIcon(policy)}
            title={formatWindDefensePolicyCardStreet(policy)}
            description={
              <Span color="quiet" size="sm">
                {formatWindDefensePolicyCardSubtitle(policy)}
              </Span>
            }
            data-testid={`wind-defense-property-${policy.keystonePolicyId}`}
          />
        ))}
      </CheckboxGroup>
      <div className={styles.wdPropertyDisclaimer}>
        <Span size="md" color="quiet">
          Only properties that are eligible to receive Wind Defense will appear
          in the list above.
        </Span>
      </div>
    </MultiStepFormLayout>
  );
};
